import React, { useState } from "react";
import {
  Button,
  TextField,
  Paper,
  Box,
  Container,
  Typography,
  Link,
} from "@mui/material";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LockResetIcon from "@mui/icons-material/LockReset";
import logo from "../assets/valonix-logo-siyah.png";
import { changePasswordRequired } from "../api/authApi";
import { setAccess, setMe } from "../redux/sessionSlice";
import { useTranslation } from "react-i18next";

const NAVY = '#1a365d';
const NAVY_HOVER = '#2c5282';

const theme = createTheme({
  palette: {
    primary: { main: NAVY, dark: NAVY_HOVER },
    secondary: { main: NAVY_HOVER },
  },
});

const buildSchema = (t) => Yup.object().shape({
  currentPassword: Yup.string().required(t("auth.firstLogin.currentRequired")),
  password: Yup.string()
    .required(t("auth.password.required"))
    .min(8, t("auth.password.min"))
    .test("has-uppercase", t("auth.password.upper"), (value) =>
      /[A-Z]/.test(value || "")
    )
    .test(
      "has-special-char",
      t("auth.password.special"),
      (value) => /[@#$%^&*()\-._+=<>?/]/.test(value || "")
    ),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], t("auth.password.mismatch"))
    .required(t("auth.password.confirmRequired")),
});

function Copyright(props) {
  return (
    <Typography variant="body2" color="text.secondary" align="center" {...props}>
      {"Copyright © "}
      <Link color="inherit" href="#" underline="hover">
        VALONIX
      </Link>{" "}
      {new Date().getFullYear()}
    </Typography>
  );
}

export default function FirstLoginPassword() {
  const { t } = useTranslation();
  const validationSchema = React.useMemo(() => buildSchema(t), [t]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const accessToken = useSelector((s) => s.session.accessToken);
  const me = useSelector((s) => s.session.me);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (values) => {
    if (!accessToken) {
      toast.error(t("auth.firstLogin.noSession"));
      navigate("/", { replace: true });
      return;
    }

    try {
      setSubmitting(true);
      const data = await changePasswordRequired(accessToken, {
        current_password: values.currentPassword,
        new_password: values.password,
        confirm_new_password: values.confirmPassword,
      });

      dispatch(setAccess({ token: data.access_token, exp: data.exp }));
      dispatch(
        setMe({
          ...me,
          must_change_password: false,
        })
      );

      toast.success(t("auth.firstLogin.success"));
      navigate("/home", { replace: true });
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const message =
        typeof detail === "string"
          ? detail
          : detail?.message || t("auth.firstLogin.error");
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: NAVY,
          padding: 2,
        }}
      >
        <Container component="main" maxWidth="sm">
          <Paper
            elevation={24}
            sx={{
              padding: { xs: 3, sm: 5 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              borderRadius: 3,
            }}
          >
            <Box
              component="img"
              src={logo}
              alt={t("auth.logoAlt")}
              sx={{ width: "50%", maxWidth: "220px", mb: 2 }}
            />
            <LockResetIcon sx={{ fontSize: 48, color: "primary.main", mb: 1 }} />
            <Typography component="h1" variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {t("auth.firstLogin.title")}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
              {t("auth.firstLogin.intro")}
            </Typography>

            <Formik
              initialValues={{
                currentPassword: "",
                password: "",
                confirmPassword: "",
              }}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              <Form style={{ width: "100%" }}>
                <Field
                  as={TextField}
                  name="currentPassword"
                  label={t("auth.firstLogin.current")}
                  type="password"
                  fullWidth
                  margin="normal"
                  autoFocus
                />
                <ErrorMessage name="currentPassword">
                  {(msg) => (
                    <Typography color="error" variant="caption">
                      {msg}
                    </Typography>
                  )}
                </ErrorMessage>

                <Field
                  as={TextField}
                  name="password"
                  label={t("auth.password.new")}
                  type="password"
                  fullWidth
                  margin="normal"
                />
                <ErrorMessage name="password">
                  {(msg) => (
                    <Typography color="error" variant="caption">
                      {msg}
                    </Typography>
                  )}
                </ErrorMessage>

                <Field
                  as={TextField}
                  name="confirmPassword"
                  label={t("auth.firstLogin.confirm")}
                  type="password"
                  fullWidth
                  margin="normal"
                />
                <ErrorMessage name="confirmPassword">
                  {(msg) => (
                    <Typography color="error" variant="caption">
                      {msg}
                    </Typography>
                  )}
                </ErrorMessage>

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  disabled={submitting}
                  sx={{ mt: 3, py: 1.5, textTransform: "none", fontWeight: 600 }}
                >
                  {submitting ? t("auth.firstLogin.saving") : t("auth.firstLogin.submit")}
                </Button>
              </Form>
            </Formik>

            <Copyright sx={{ mt: 3 }} />
          </Paper>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
